import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Check,
  X,
  Trash2,
  CalendarClock,
  Phone,
  Edit,
  MoreVertical,
  Eye,
  MapPin,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import PropTypes from "prop-types"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { AppointmentDetailsSheet } from "@/components/appointments/AppointmentDetailsSheet"
import { useState } from "react"
import { Link } from "react-router-dom"

const formatDateTime = (dateString) => {
  const date = new Date(dateString)
  return {
    date: date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  }
}

const getStatusBadge = (status) => {
  const badges = {
    PENDING: "bg-yellow-100 text-yellow-800",
    APPROVED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
    COMPLETED: "bg-blue-100 text-blue-800",
  }
  const labels = {
    PENDING: "Pending",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    COMPLETED: "Completed",
  }
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}
    >
      {labels[status]}
    </span>
  )
}

function AppointmentCard({
  appointment,
  onAccept,
  onReject,
  onDelete,
  onEdit,
  onComplete,
}) {
  const { date, time } = formatDateTime(appointment.appointment_date)
  const [showDetails, setShowDetails] = useState(false)

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden">
      <CardContent className="p-4 sm:p-6">
        <div className="flex justify-between items-start gap-4">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`/patients/${appointment.patient_id}`}
                      className="text-base sm:text-lg font-semibold hover:text-blue-600 transition-colors line-clamp-2 break-words"
                    >
                      {appointment.patient_name}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{appointment.patient_name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {getStatusBadge(appointment.appointment_status)}
            </div>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/doctors/${appointment.doctor_id}`}
                    className="text-blue-600 font-medium text-sm hover:text-blue-800 transition-colors line-clamp-2 break-words"
                  >
                    {appointment.doctor_name}
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{appointment.doctor_name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <p className="text-sm text-gray-500 line-clamp-2">
              {appointment.symptoms}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {appointment.appointment_status === "PENDING" && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-green-200 hover:border-green-500 hover:bg-green-50"
                        onClick={() => onAccept(appointment)}
                      >
                        <Check className="h-4 w-4 text-green-500" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Accept</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-red-200 hover:border-red-500 hover:bg-red-50"
                        onClick={() => onReject(appointment)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reject</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(appointment)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                {appointment.appointment_status === "APPROVED" && (
                  <DropdownMenuItem onClick={() => onComplete(appointment)}>
                    <Check className="mr-2 h-4 w-4" />
                    Mark as Completed
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => onDelete(appointment)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center text-xs sm:text-sm text-gray-600">
            <Phone className="h-4 w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
            <span className="truncate">{appointment.mobile_number}</span>
          </div>
          <div className="flex items-center text-xs sm:text-sm text-gray-600">
            <CalendarClock className="h-4 w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
            <div className="text-gray-500">
              <span className="font-medium">{date}</span>
              <span className="mx-2">at</span>
              <span className="font-medium">{time}</span>
            </div>
          </div>
          {appointment.location && (
            <div className="flex items-center text-xs sm:text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
              <span>{appointment.location}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <Button
            variant="outline"
            className="w-full text-xs sm:text-sm"
            onClick={() => setShowDetails(true)}
          >
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>
        </div>
      </CardContent>

      <AppointmentDetailsSheet
        appointment={appointment}
        open={showDetails}
        onOpenChange={setShowDetails}
      />
    </Card>
  )
}

AppointmentCard.propTypes = {
  appointment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    patient_name: PropTypes.string.isRequired,
    doctor_name: PropTypes.string.isRequired,
    symptoms: PropTypes.string.isRequired,
    mobile_number: PropTypes.string.isRequired,
    appointment_date: PropTypes.string.isRequired,
    appointment_status: PropTypes.string.isRequired, // Changed from number to string
    location: PropTypes.string,
  }).isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
}

export default AppointmentCard
